import { useCallback, useEffect, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import { FaArrowRightArrowLeft } from "react-icons/fa6";
import Pagination from "../../components/tables/Pagination";
import Token from "../../database/Token";
import { toastCfg } from "../../data/toastCfg";

const statusStyles = {
  pending: "bg-amber-100 text-amber-700",
  approved: "bg-green-100 text-green-700",
  rejected: "bg-red-100 text-red-700",
};

export default function AllRequestLeads() {
  const [requests, setRequests] = useState([]);
  const [status, setStatus] = useState("pending");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [reviewingId, setReviewingId] = useState(null);

  const fetchRequests = useCallback(async () => {
    setLoading(true);

    try {
      const response = await Token.get("/lead-transfer-requests", {
        params: {
          status,
          search,
          page,
          limit: rowsPerPage,
        },
      });
      const result = response.data.data;
      setRequests(result.data || []);
      setTotal(result.total || 0);
      setTotalPages(result.last_page || 1);
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Failed to load lead requests.",
        toastCfg
      );
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, status]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const reviewRequest = async (requestRow, action) => {
    let reason = "";

    if (action === "reject") {
      reason = window.prompt("Reason for rejecting this request:")?.trim();
      if (!reason) return;
    }

    setReviewingId(requestRow.id);

    try {
      const response = await Token.patch(
        `/lead-transfer-requests/${requestRow.id}/review`,
        { action, reason }
      );
      toast.success(response.data.message, toastCfg);
      fetchRequests();
    } catch (err) {
      toast.error(
        err?.response?.data?.message || `Failed to ${action} request.`,
        toastCfg
      );
    } finally {
      setReviewingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <ToastContainer {...toastCfg} />

      <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-[20px] bg-primary-100 text-primary-500 dark:bg-primary-900/20">
            <FaArrowRightArrowLeft size={20} />
          </div>
          <div>
            <h1 className="text-3xl font-black text-gray-800 dark:text-white">
              Lead Transfer Requests
            </h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Review requests to move old leads to another team member.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <input
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            placeholder="Search lead or requester"
            className="h-11 min-w-64 rounded-2xl border border-surface-border bg-white px-4 outline-none focus:border-primary-500 dark:border-surface-darkBorder dark:bg-surface-darkMuted"
          />
          <select
            value={status}
            onChange={(event) => {
              setStatus(event.target.value);
              setPage(1);
            }}
            className="h-11 rounded-2xl border border-surface-border bg-white px-4 dark:border-surface-darkBorder dark:bg-surface-darkMuted"
          >
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      </div>

      <div className="overflow-hidden rounded-[32px] border border-surface-border bg-white shadow-sm dark:border-surface-darkBorder dark:bg-surface-darkCard">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-surface-soft text-left text-sm text-gray-600 dark:bg-surface-darkMuted dark:text-gray-300">
              <tr>
                <th className="px-5 py-4">#</th>
                <th className="px-5 py-4">Lead</th>
                <th className="px-5 py-4">Current Owner</th>
                <th className="px-5 py-4">Requested By</th>
                <th className="px-5 py-4">Requested At</th>
                <th className="px-5 py-4">Status</th>
                <th className="px-5 py-4">Reviewed By</th>
                <th className="px-5 py-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="py-16 text-center text-gray-500">
                    Loading requests...
                  </td>
                </tr>
              ) : requests.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-16 text-center text-gray-500">
                    No {status} requests found.
                  </td>
                </tr>
              ) : (
                requests.map((row, index) => (
                  <tr
                    key={row.id}
                    className="border-t border-surface-border dark:border-surface-darkBorder"
                  >
                    <td className="px-5 py-4">
                      {(page - 1) * rowsPerPage + index + 1}
                    </td>
                    <td className="px-5 py-4">
                      <p className="font-bold text-gray-800 dark:text-white">
                        {row.lead?.name || "Deleted lead"}
                      </p>
                      <p className="text-xs text-gray-500">
                        {row.lead?.lead_id} · {row.lead?.company || "No company"}
                      </p>
                    </td>
                    <td className="px-5 py-4">
                      {row.previous_assignee?.name || "-"}
                    </td>
                    <td className="px-5 py-4">
                      <p className="font-semibold">{row.requester?.name || "-"}</p>
                      <p className="text-xs text-gray-500">
                        {row.requester?.type}
                      </p>
                    </td>
                    <td className="px-5 py-4">
                      {new Date(row.created_at).toLocaleString()}
                    </td>
                    <td className="px-5 py-4">
                      <span
                        className={`rounded-full px-3 py-1 text-xs font-bold capitalize ${
                          statusStyles[row.status]
                        }`}
                      >
                        {row.status}
                      </span>
                      {row.rejection_reason && (
                        <p className="mt-2 max-w-56 text-xs text-red-600">
                          {row.rejection_reason}
                        </p>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      {row.reviewer?.name || "-"}
                    </td>
                    <td className="px-5 py-4">
                      {row.status === "pending" ? (
                        <div className="flex gap-2">
                          <button
                            type="button"
                            disabled={reviewingId === row.id}
                            onClick={() => reviewRequest(row, "approve")}
                            className="rounded-xl bg-green-600 px-4 py-2 text-sm font-bold text-white hover:bg-green-700 disabled:opacity-50"
                          >
                            Approve
                          </button>
                          <button
                            type="button"
                            disabled={reviewingId === row.id}
                            onClick={() => reviewRequest(row, "reject")}
                            className="rounded-xl bg-red-100 px-4 py-2 text-sm font-bold text-red-700 hover:bg-red-200 disabled:opacity-50"
                          >
                            Reject
                          </button>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500">
                          {row.reviewed_at
                            ? new Date(row.reviewed_at).toLocaleString()
                            : "-"}
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        rowsPerPage={rowsPerPage}
        onRowsChange={(value) => {
          setRowsPerPage(value);
          setPage(1);
        }}
        total={total}
      />
    </div>
  );
}
